// views/Dashboard/Bonuses/components/EarningModal/EarningModal.tsx
import { useState, type FormEvent, type ReactNode } from 'react';
import Button from '../../../../../components/common/Button/Button';
import Modal from '../../../../../components/common/Modal/Modal';
import { BonusHistoryObj, BonusProgress } from '../../../../../Entities';
import { notify } from '../../../../../components/common/Toast/toastBridge';
import styles from './EarningModal.module.css';

interface EarningsModalProps {
  onBonusFilled: () => void;
  selectedBonus: any;
  user: any;
  setUser: (user: any) => void;
  setShowEarningsModal: (show: boolean) => void;
}

const ModalWrapper = ({ onClose, children, title }: { onClose: () => void; children: ReactNode; title: string }) => (
  <Modal isOpen onClose={onClose} title={title}>
    <div className={styles.modalContent}>{children}</div>
  </Modal>
);

const EarningsModal = ({ onBonusFilled, selectedBonus, user, setUser, setShowEarningsModal }: EarningsModalProps) => {
  const [reachedProfit, setReachedProfit] = useState('');
  const [endBalance, setEndBalance] = useState('');
  const [startBalance, setStartBalance] = useState<string>(
    (selectedBonus.start_balance && selectedBonus.start_balance.toString()) || '',
  );
  const [inCash, setInCash] = useState('0');
  const [outCash, setOutCash] = useState('0');
  const [clicked, setClicked] = useState(false);

  const createBonusHistory = async () => {
    try {
      let reached_profit = 0;
      if (startBalance) {
        const correction = parseFloat(inCash) - parseFloat(outCash);
        reached_profit = parseFloat(endBalance) - correction - parseFloat(startBalance);
      } else {
        reached_profit = parseFloat(reachedProfit);
      }

      await BonusHistoryObj.create({ bonusId: selectedBonus.id, amount: reached_profit, note: '' });

      user.bonus_revenue += reached_profit;
      user.potential_bonus_revenue += parseFloat(selectedBonus.expected_value);
      user.bonus_history_len++;
      setUser(user);
    } catch (error) {
      console.error(error);
      notify.error('Nepodařilo se dokončit bonus');
    }
  };

  const updateStartBalance = async () => {
    try {
      await BonusProgress.updateStartBalance(selectedBonus.id, Number(startBalance));
      selectedBonus.start_balance = startBalance;
      setShowEarningsModal(false);
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      setClicked(false);
    }
  };

  const handleSaveBalance = async (e: FormEvent) => {
    e.preventDefault();
    setClicked(true);
    await updateStartBalance();
  };

  const handleBonusHistoryCreation = async (e: FormEvent) => {
    e.preventDefault();
    await createBonusHistory();
    onBonusFilled();
    setClicked(false);
  };

  const isStartingBonus = selectedBonus.step_idx === 0 && selectedBonus.start_balance === null;
  const hasStartBalance = selectedBonus.start_balance !== null;

  if (isStartingBonus) {
    return (
      <ModalWrapper onClose={() => setShowEarningsModal(false)} title="Uložte si počáteční zůstatek">
        <form className={styles.earningsForm} data-tour="start-balance-form">
          <input
            type="number"
            className="form-input"
            placeholder="Počáteční zůstatek"
            value={startBalance}
            onChange={(e) => setStartBalance(e.target.value)}
            required
          />
          <div className={styles.actions}>
            <Button variant="ghost" disabled={clicked} onClick={() => setShowEarningsModal(false)}>
              Neukládat
            </Button>
            <Button variant="primary" disabled={clicked || !startBalance} onClick={handleSaveBalance}>
              Uložit zůstatek
            </Button>
          </div>
        </form>
      </ModalWrapper>
    );
  }

  if (hasStartBalance) {
    return (
      <ModalWrapper onClose={() => setShowEarningsModal(false)} title="Vypočítejte si zisk">
        <form className={styles.earningsForm}>
          <label>Počáteční zůstatek (Kč):</label>
          <input type="number" className="form-input" placeholder="Počáteční zůstatek" value={startBalance} onChange={(e) => setStartBalance(e.target.value)} required />
          <label>Konečný zůstatek (Kč):</label>
          <input type="number" className="form-input" placeholder="Konečný zůstatek" value={endBalance} onChange={(e) => setEndBalance(e.target.value)} required />
          <label>Vklady (Kč):</label>
          <input type="number" className="form-input" placeholder="Vklady" value={inCash} onChange={(e) => setInCash(e.target.value)} required />
          <label>Výběry (Kč):</label>
          <input type="number" className="form-input" placeholder="Výběry" value={outCash} onChange={(e) => setOutCash(e.target.value)} required />
          <Button
            onClick={(e) => {
              setClicked(true);
              handleBonusHistoryCreation(e);
            }}
            disabled={clicked || !startBalance || !endBalance || !inCash || !outCash}
          >
            Uložit výdělek
          </Button>
        </form>
      </ModalWrapper>
    );
  }

  return (
    <ModalWrapper onClose={() => setShowEarningsModal(false)} title="Zadat výdělek z bonusu">
      <form className={styles.earningsForm}>
        <label>Částka výdělku (Kč):</label>
        <input type="number" className="form-input" placeholder="500" value={reachedProfit} onChange={(e) => setReachedProfit(e.target.value)} required />
        <Button
          onClick={(e) => {
            setClicked(true);
            handleBonusHistoryCreation(e);
          }}
          disabled={clicked || !reachedProfit}
        >
          Uložit výdělek
        </Button>
      </form>
    </ModalWrapper>
  );
};

export default EarningsModal;
